// Leaflet's stylesheet is bundled by Vite (was an unpkg <link> in index.html —
// a render-blocking third-party fetch and an availability dependency).
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./lib/leaflet-side-by-side";
import { loadCatalog, type CatalogLayer, CATEGORY_ORDER, CATEGORY_TITLE } from "./catalog";
import { createLayer, setDecodeErrorSink } from "./layers";
import { readPermalink, writePermalink, type PermalinkState } from "./permalink";

// ----- Map init -----
const map = L.map("map", { zoomControl: true, preferCanvas: false }).setView([42.38, -71.11], 13);

// Dedicated pane so layers sit above the default tilePane regardless of
// activation order. tilePane is z-index 200; ours goes to 350.
const OVERLAY_PANE = "mapbeast-overlays";
map.createPane(OVERLAY_PANE).style.zIndex = "350";

// Loading indicator — a subtle pill shown while any active overlay is still
// fetching tiles, so slow connections read as "loading" rather than "broken".
// COG GridLayers and xyz/arcgis-dynamic TileLayers all fire Leaflet's
// "loading"/"load" events, so this covers every layer type. A per-layer safety
// timeout clears a stalled layer (broken upstream) so the pill never sticks.
// watchLoading() is wired in activateLayer right after addTo(map).
const loadingLayers = new Set<L.Layer>();
const loadingEl = document.createElement("div");
loadingEl.id = "loading-indicator";
loadingEl.innerHTML = '<span class="spinner" aria-hidden="true"></span><span>Loading imagery…</span>';
loadingEl.hidden = true;
document.getElementById("map")!.appendChild(loadingEl);
// Debounced: only reveal the pill after ~300ms of continuous loading, so a fast
// connection (tiles in well under 300ms) never flickers it; hide immediately when done.
let showTimer: ReturnType<typeof setTimeout> | undefined;
function refreshLoading(): void {
  if (loadingLayers.size > 0) {
    if (loadingEl.hidden && !showTimer) {
      showTimer = setTimeout(() => { loadingEl.hidden = false; showTimer = undefined; }, 300);
    }
  } else {
    if (showTimer) { clearTimeout(showTimer); showTimer = undefined; }
    loadingEl.hidden = true;
  }
}
function watchLoading(l: L.Layer): void {
  let stall: ReturnType<typeof setTimeout> | undefined;
  const clear = () => {
    loadingLayers.delete(l);
    if (stall) { clearTimeout(stall); stall = undefined; }
    refreshLoading();
  };
  l.on("loading", () => {
    loadingLayers.add(l);
    if (stall) clearTimeout(stall);
    stall = setTimeout(clear, 30000); // never let a stalled/broken layer pin the pill
    refreshLoading();
  });
  l.on("load", clear);
  l.on("remove", clear);
}

// State
const catalog = await loadCatalog();
// `layers` is empty ([]) only transiently: a placeholder slot reserved by
// activateLayer while createLayer() is awaiting (see activateLayer for rationale).
// It holds >1 layer when the entry is a GROUP (one picker unit backed by several
// member COG/tile layers — atlas plates, Sanborn volumes shown as one).
const activeLayers = new Map<string, { layers: L.Layer[]; entry: CatalogLayer; opacity: number }>();
let swipeControl: any = null;
let swipePair: [string, string] | null = null;

// Apply view from permalink (active layers restored after UI init below).
const pl = readPermalink();
if (pl.center && pl.zoom != null) map.setView(pl.center, pl.zoom);

// ----- UI: layer picker -----
const picker = document.getElementById("layer-picker")!;
const activeSection = document.getElementById("active-section")!;
const searchInput = document.getElementById("layer-search") as HTMLInputElement;
const showAllToggle = document.getElementById("show-all-toggle") as HTMLInputElement;
// Resolved bounds per layer. `bounds` (geometry) is the only locality primitive;
// a layer with no bounds is world-scope (always relevant — OSM, Esri, US-wide topo).
const layerBounds = new Map<string, L.LatLngBounds | null>();
for (const l of catalog.layers) {
  layerBounds.set(l.id, l.bounds ? L.latLngBounds(l.bounds as L.LatLngBoundsLiteral) : null);
}
// Sparse: only layers carrying a `coverage` field (~300 regional layers of ~20k).
// Everything else takes the single-bbox fast path through layerBounds — no
// per-layer allocation for the common case.
const layerCoverage = new Map<string, L.LatLngBounds[]>();
for (const l of catalog.layers) {
  if (l.coverage?.length) {
    layerCoverage.set(l.id, l.coverage.map((b) => L.latLngBounds(b as L.LatLngBoundsLiteral)));
  }
}

// --- Layer grouping + auto date-cells ---------------------------------------
// `displayLayers` is what the PICKER iterates; `catalog.layers` stays the real
// list for createLayer + by-id lookups; `unitById` resolves either. Two collapses
// into synthetic units with `members` (real layers) that activateLayer fans out.
// Backward-compatible: nothing eligible ⇒ displayLayers === catalog.layers.
//   (1) HAND groups: layers sharing `group_id` → one entry (curated plate sets).
//   (2) AUTO date-cells: a region's aerials / historic-maps of one DECADE collapse
//       into a "1890s maps" entry — but ONLY the mutually NON-OVERLAPPING pieces
//       (disjoint bboxes can't visually overlap, so disparate map types tiling an
//       area is fine = the coverage view). Overlapping alternatives stay as their
//       own rows (multiple choices per decade); singles stay individual; year:0
//       junk + topo + reference are excluded. We only have bboxes (not footprints),
//       so the strict overlap cap errs toward NOT compositing — never a visual stack.
// Plain numeric bbox — the clustering below runs over ~20k layers at startup,
// so its geometry predicates work on these (populated into `numBounds` after
// grouping) instead of calling L.LatLngBounds getters / allocating padded
// bounds per comparison.
type NumBounds = { s: number; w: number; n: number; e: number };
const ovFrac = (a: NumBounds, b: NumBounds): number => {
  const s = Math.max(a.s, b.s), n = Math.min(a.n, b.n);
  const w = Math.max(a.w, b.w), e = Math.min(a.e, b.e);
  const ar = (x: NumBounds) => (x.n - x.s) * (x.e - x.w);
  const m = Math.min(ar(a), ar(b));
  return m > 0 ? (Math.max(0, n - s) * Math.max(0, e - w)) / m : 1;
};
const unionBounds = (ids: string[]): L.LatLngBounds | null => {
  let u: L.LatLngBounds | null = null;
  for (const id of ids) {
    const b = layerBounds.get(id);
    if (b) u = u ? u.extend(b) : L.latLngBounds(b.getSouthWest(), b.getNorthEast());
  }
  return u;
};
const MAX_CELL_OVERLAP = 0.04;  // bbox overlap below this ⇒ effectively disjoint ⇒ no visual stack.
// (Was 0.08, which collapsed two genuinely-overlapping standalone maps into one cell —
//  e.g. "Boston, MA 1776" vs "Boston Harbor (Sayer) 1777" share ~6% downtown/harbor and
//  should stay separate picker entries. True tiling sets abut at <4%, so 0.04 keeps them.)

// (1) hand-groups → atoms (a hand-group is one atom, flattened if it joins a cell)
const groupUnits = new Map<string, CatalogLayer>();
const atoms: CatalogLayer[] = [];
for (const l of catalog.layers) {
  if (!l.group_id) { atoms.push(l); continue; }
  let g = groupUnits.get(l.group_id);
  if (!g) { g = { ...l, id: l.group_id, title: l.group_title || l.group_id, members: [] }; groupUnits.set(l.group_id, g); atoms.push(g); }
  g.members!.push(l);
}
for (const g of groupUnits.values()) {
  const u = unionBounds(g.members!.map((m) => m.id));
  layerBounds.set(g.id, u);
  g.bounds = u ? [[u.getSouth(), u.getWest()], [u.getNorth(), u.getEast()]] : undefined;
}

// Hoisted per-atom geometry (computed once, AFTER group bounds land above):
// the clustering loops below used to re-read L.LatLngBounds getters — and
// allocate a fresh padded LatLngBounds per adjacent() test — inside O(k²)
// walks, which dominated startup at catalog scale.
const numBounds = new Map<string, NumBounds>();
for (const a of atoms) {
  const b = layerBounds.get(a.id);
  if (b) numBounds.set(a.id, { s: b.getSouth(), w: b.getWest(), n: b.getNorth(), e: b.getEast() });
}
// Numeric mirror of `layerCoverage` for the clustering predicates below (real
// layers only — groups/cells fall back to their union bbox in numBounds).
const numCoverage = new Map<string, NumBounds[]>();
for (const l of catalog.layers) {
  if (l.coverage?.length) {
    numCoverage.set(l.id, l.coverage.map(([[s, w], [n, e]]) => ({ s, w, n, e })));
  }
}

// (2) Cells are GEOMETRIC, not region-keyed. `region` is an arbitrary hand-drawn tag
// (metro vs county vs city, overlapping/nested) — using it to group cells reshuffles
// them whenever a region is re-drawn. Instead, bin only by (modality, decade), then
// cluster by WHERE THINGS ACTUALLY ARE: connected-components over bbox overlap-or-
// adjacency. Each locality cluster is then partitioned into maximal non-overlapping
// cells (overlapping pieces → separate cells = multiple choices per decade). Every
// eligible dated layer becomes a cell (even a lone one). Region drives nothing here.
const cellModality = (l: CatalogLayer): "aerial" | "map" | null =>
  l.category === "imagery" ? "aerial"
  : (l.category === "historical" && !/htmc|topo|usgs-ortho/.test(l.id)) ? "map"
  : null;
// Two bboxes "sit together" if they overlap or their gap is under an ABSOLUTE
// ~4 km — not a fraction of bbox size (that let huge statewide bboxes reach across
// state lines and chained scattered maps into 15°-wide blobs). Touching Sanborn
// plates/volumes still connect; maps a few km apart stay separate local cells.
const GAP_DEG = 0.04;
// A layer wider than this in EITHER dimension is REGIONAL, not local: statewide /
// multi-county coverage (NY NAIP is 4.5°×7.9°, MassGIS 1.7°×3.7°, King County 4°×10°).
// ~0.6° ≈ 65 km N-S — comfortably bigger than any single metro's imagery footprint
// and well under a state's.
const REGIONAL_DEG = 0.6;
const isRegional = (l: CatalogLayer): boolean => {
  const b = numBounds.get(l.id);
  if (!b) return false;
  return (b.n - b.s) > REGIONAL_DEG
      || (b.e - b.w) > REGIONAL_DEG;
};
// isRegional is deterministic per atom — resolve it once so the id-anchor
// selection below is a set lookup, not a find-inside-find.
const regionalIds = new Set(atoms.filter(isRegional).map((a) => a.id));
// Does `big` actually contain `small`? Used to decide whether a regional layer
// belongs to a locality. Most-of, not all-of: a statewide bbox can clip a metro that
// straddles a state line (Kansas City, Cincinnati), and we still want the statewide
// layer offered there.
const COVER_FRAC = 0.6;
const covers = (big: CatalogLayer, small: CatalogLayer): boolean => {
  const S = numBounds.get(small.id);
  if (!S) return false;
  const sArea = (S.n - S.s) * (S.e - S.w);
  if (sArea <= 0) return false;
  // With a `coverage` field, sum intersections over big's real-content boxes
  // (exact — boxes are non-overlapping): a statewide layer no longer "covers"
  // a locality that sits in its bbox's empty corner (Michigan-over-Naperville).
  const B = numBounds.get(big.id);
  const boxes = numCoverage.get(big.id) ?? (B ? [B] : null);
  if (!boxes) return false;
  let inter = 0;
  for (const b of boxes) {
    const iw = Math.min(b.e, S.e) - Math.max(b.w, S.w);
    const ih = Math.min(b.n, S.n) - Math.max(b.s, S.s);
    if (iw > 0 && ih > 0) inter += iw * ih;
  }
  return inter / sArea >= COVER_FRAC;
};
// Two bboxes "sit together" if they overlap or their gap is under GAP_DEG.
// Numeric equivalent of `pad(bx, GAP_DEG).intersects(by)` (Leaflet intersects
// is inclusive — >= / <= — so this matches the old L.LatLngBounds test exactly)
// without allocating a padded LatLngBounds per comparison.
const adjacentNB = (bx: NumBounds, by: NumBounds): boolean =>
  by.n >= bx.s - GAP_DEG && by.s <= bx.n + GAP_DEG &&
  by.e >= bx.w - GAP_DEG && by.w <= bx.e + GAP_DEG;
const cellArea = (l: CatalogLayer) => { const b = numBounds.get(l.id)!; return (b.n - b.s) * (b.e - b.w); };
const bins = new Map<string, CatalogLayer[]>();
const displayLayers: CatalogLayer[] = [];
// A regional layer joins EVERY locality it covers, so a statewide aerial can be the
// lone occupant of a cell in a dozen neighbouring localities. Those cells all render
// as the same generic "1940s aerials" row AND carry distinct ids, so the same layer
// could be activated twice, filling both active slots with one layer (seen over
// Orlando: 8 identical "1940s aerials" rows, all Florida Statewide Aerial 1940s).
// A cell that collapses to a SINGLE unit is not a cell at all — emit that unit
// directly, once, under its own real title.
const emittedSingle = new Set<string>();
for (const a of atoms) {
  const mod = cellModality(a), yr = a.year, b = layerBounds.get(a.id);
  if (mod && typeof yr === "number" && yr > 0 && b) {
    const k = `${mod}|${Math.floor(yr / 10) * 10}`;
    (bins.get(k) ?? bins.set(k, []).get(k)!).push(a);
  } else displayLayers.push(a);
}
for (const [k, binAtoms] of bins) {
  const [mod, dec] = k.split("|");
  const modLabel = mod === "aerial" ? "aerials" : "maps";
  // Localities are discovered from LOCAL layers only; regional layers are attached
  // afterwards. Connected-components is transitive, so letting a statewide bbox
  // participate in the walk glues together every city it covers — Somerville chained
  // to Rochester via massgis-2019 → nyits-naip-2019 → roc-pictometry-2012, producing a
  // "2010s aerials (5)" cell over Somerville whose members were MassGIS, Syracuse,
  // Pittsburgh, NYC and Rochester (four of them 300+ km away) while Cambridge's own
  // aerial landed in a different cell. Excluding regional layers from the walk also
  // stops them acting as an INTERMEDIARY between two distant locals: Cambridge and
  // Nashua NH sit 0.297° apart with no lon overlap — nowhere near the 0.04° gap rule —
  // yet both touch the MassGIS statewide bbox, so a "regional may join but not bridge"
  // rule still merged them. They must simply never meet.
  const localAtoms = binAtoms.filter((a) => !regionalIds.has(a.id));
  const regionalAtoms = binAtoms.filter((a) => regionalIds.has(a.id));
  // Spatial hash for the component walk: bucket each local atom into every grid
  // cell its bbox overlaps, so each popped node only tests atoms in the cells
  // its GAP_DEG-padded bbox reaches — instead of rescanning all of localAtoms
  // (O(k²) per bin, the old startup hot spot). The exact adjacentNB test still
  // decides membership, so clustering semantics are unchanged; the grid only
  // prunes candidates. Cell size just needs to comfortably exceed GAP_DEG —
  // local atoms are < REGIONAL_DEG per side, so most span 1–4 cells.
  const CELL_DEG = 0.25;
  const grid = new Map<string, CatalogLayer[]>();
  for (const a of localAtoms) {
    const nb = numBounds.get(a.id)!; // binned atoms always have bounds (checked at binning)
    for (let cy = Math.floor(nb.s / CELL_DEG); cy <= Math.floor(nb.n / CELL_DEG); cy++)
      for (let cx = Math.floor(nb.w / CELL_DEG); cx <= Math.floor(nb.e / CELL_DEG); cx++) {
        const k = `${cx}:${cy}`;
        (grid.get(k) ?? grid.set(k, []).get(k)!).push(a);
      }
  }
  const seen = new Set<CatalogLayer>();
  for (const start of localAtoms) {
    if (seen.has(start)) continue;
    const comp: CatalogLayer[] = [], queue = [start]; seen.add(start); // connected-component (locality)
    while (queue.length) {
      const cur = queue.pop()!; comp.push(cur);
      const nb = numBounds.get(cur.id)!;
      for (let cy = Math.floor((nb.s - GAP_DEG) / CELL_DEG); cy <= Math.floor((nb.n + GAP_DEG) / CELL_DEG); cy++)
        for (let cx = Math.floor((nb.w - GAP_DEG) / CELL_DEG); cx <= Math.floor((nb.e + GAP_DEG) / CELL_DEG); cx++) {
          const bucket = grid.get(`${cx}:${cy}`);
          if (!bucket) continue;
          for (const o of bucket) if (!seen.has(o) && adjacentNB(nb, numBounds.get(o.id)!)) { seen.add(o); queue.push(o); }
        }
    }
    // Attach every regional layer that genuinely COVERS this locality. Each locality
    // gets its own copy — that's the point: statewide imagery is a legitimate choice
    // over Somerville AND over Rochester, without making them the same place.
    //
    // Coverage, not adjacency: `adjacent()` is a 4 km GAP test, which is right for
    // deciding that two neighbouring towns belong together but wrong here. Metro-scale
    // layers land on the regional side of any single threshold (Syracuse is 0.86°,
    // Pittsburgh 0.71° — both > REGIONAL_DEG yet plainly not statewide), and under a
    // gap test they'd bolt onto whatever locality they happened to touch: over
    // Somerville the picker still offered "2010s aerials (3)" = MassGIS + Allegheny
    // County + Syracuse. Requiring the regional bbox to actually contain most of the
    // locality's footprint makes the threshold's exact value stop mattering — a
    // too-small metro layer simply attaches to nothing but its own place.
    for (const r of regionalAtoms) if (comp.some((c) => covers(r, c))) comp.push(r);
    let remaining = comp.sort((x, y) => cellArea(y) - cellArea(x)); // biggest first
    while (remaining.length) {
      const picked: CatalogLayer[] = [], next: CatalogLayer[] = [];
      for (const c of remaining) {
        const cb = numBounds.get(c.id)!;
        (picked.every((p) => ovFrac(cb, numBounds.get(p.id)!) < MAX_CELL_OVERLAP) ? picked : next).push(c);
      }
      if (picked.length === 1) {
        // Single unit -> show the layer/hand-group itself, not a synthetic cell.
        // Deduped by id: the same regional layer reaches here once per locality.
        const only = picked[0];
        if (!emittedSingle.has(only.id)) { emittedSingle.add(only.id); displayLayers.push(only); }
        remaining = next;
        continue;
      }
      const members = picked.flatMap((p) => p.members ?? [p]);
      const u = unionBounds(picked.map((p) => p.id));
      // Id is stable per member SET, and must be UNIQUE across cells. A regional
      // layer now joins every locality it covers, so the old smallest-id-alone
      // scheme collides with itself (statewide NAIP anchors a cell in 20 metros).
      // Anchor on the smallest LOCAL id; when a cell is ALL regional there is no
      // local anchor, so disambiguate with the locality's own anchor id — the
      // component's smallest local id, or the cell's own bbox if the whole
      // component is regional. Verified collision-free over the full catalog
      // (1831 cells, 0 duplicates); a collision would make permalinks resolve to
      // the wrong cell, so keep that check if this logic changes.
      const idsSorted = [...picked].map((p) => p.id).sort();
      const localAnchor = idsSorted.find((pid) => !regionalIds.has(pid));
      const compLocal = comp.filter((cc) => !regionalIds.has(cc.id)).map((cc) => cc.id).sort()[0];
      const id = localAnchor
        ? `cell:${mod}:${dec}:${localAnchor}`
        : `cell:${mod}:${dec}:${compLocal ?? "x"}:${idsSorted[0]}`;
      layerBounds.set(id, u);
      displayLayers.push({
        ...picked[0], id, members, year: Number(dec),
        title: `${dec}s ${modLabel}${members.length > 1 ? ` (${members.length})` : ""}`,
        bounds: u ? [[u.getSouth(), u.getWest()], [u.getNorth(), u.getEast()]] : undefined,
        group_id: undefined, group_title: undefined, // synthetic cell, not a hand-group member
      });
      remaining = next;
    }
  }
  // A regional layer covering NO local layer in its bin (e.g. a statewide aerial for
  // a state where we hold no city imagery that decade) would otherwise be dropped
  // from the picker entirely, since localities drive the walk. Emit it as its own cell.
  for (const r of regionalAtoms) {
    if (localAtoms.some((c) => covers(r, c))) continue;
    // Same rule as above: a lone regional layer is its own row, under its own title.
    if (!r.members || r.members.length <= 1) {
      if (!emittedSingle.has(r.id)) { emittedSingle.add(r.id); displayLayers.push(r); }
      continue;
    }
    const members = r.members ?? [r];
    const b = layerBounds.get(r.id) ?? null;
    const cid = `cell:${mod}:${dec}:${r.id}`;
    layerBounds.set(cid, b); // else the viewport filter treats it as world-scope
    displayLayers.push({
      ...r, id: cid, members, year: Number(dec),
      title: `${dec}s ${modLabel}${members.length > 1 ? ` (${members.length})` : ""}`,
      bounds: b ? [[b.getSouth(), b.getWest()], [b.getNorth(), b.getEast()]] : undefined,
      group_id: undefined, group_title: undefined,
    });
  }
}
const unitById = new Map(displayLayers.map((l) => [l.id, l]));
// Also resolve a MEMBER's own id (a layer collapsed into an auto date-cell or a hand
// group) to that single layer, so a permalink/direct link can isolate one member
// (e.g. #layers=roc-plat-1858:1 to see just the Gillette plat, not the whole "1850s
// maps" cell). Members keep their own id; the cell/group unit owns the collapsed id.
for (const u of displayLayers)
  if (u.members) for (const m of u.members) if (!unitById.has(m.id)) unitById.set(m.id, m);

// Restore show-all preference from localStorage (UI pref, not view state)
showAllToggle.checked = localStorage.getItem("mapbeast.showAll") === "true";
showAllToggle.addEventListener("change", () => {
  localStorage.setItem("mapbeast.showAll", String(showAllToggle.checked));
  renderPicker();
});

let searchQuery = "";
let searchDebounce: number | undefined;
searchInput.addEventListener("input", () => {
  window.clearTimeout(searchDebounce);
  searchDebounce = window.setTimeout(() => {
    searchQuery = searchInput.value.trim().toLowerCase();
    renderPicker();
  }, 120);
});

// ===== Location search — find a place by name, address, or lat/lon =====
const locationInput = document.getElementById("location-search") as HTMLInputElement;
const locationResults = document.getElementById("location-results")!;
let geocodeDebounce: number | undefined;
let geocodeSeq = 0; // bumped per query so stale fetch responses are dropped

interface GeoResult { label: string; go: () => void; }

function closeLocationResults(): void {
  locationResults.classList.remove("show");
  locationResults.innerHTML = "";
}
function renderLocationResults(items: GeoResult[]): void {
  locationResults.innerHTML = "";
  if (!items.length) { closeLocationResults(); return; }
  for (const it of items) {
    const row = document.createElement("div");
    row.className = "location-result";
    row.textContent = it.label;
    row.addEventListener("click", () => { it.go(); closeLocationResults(); locationInput.blur(); });
    locationResults.appendChild(row);
  }
  locationResults.classList.add("show");
}

// "lat,lon" or "lat lon" → [lat,lon], else null.
function parseLatLon(s: string): [number, number] | null {
  const m = s.match(/^\s*(-?\d{1,3}(?:\.\d+)?)\s*[, ]\s*(-?\d{1,3}(?:\.\d+)?)\s*$/);
  if (!m) return null;
  const lat = +m[1], lon = +m[2];
  return lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180 ? [lat, lon] : null;
}

async function runGeocode(q: string): Promise<void> {
  const ll = parseLatLon(q);
  if (ll) {
    renderLocationResults([{
      label: `📍 ${ll[0].toFixed(5)}, ${ll[1].toFixed(5)}`,
      go: () => map.setView(ll, 14),
    }]);
    return;
  }
  if (q.length < 3) { closeLocationResults(); return; }
  const seq = ++geocodeSeq;
  try {
    // Nominatim (OSM) — catalog is all US, so bias results to the US.
    const url = "https://nominatim.openstreetmap.org/search?format=jsonv2&limit=6"
      + `&countrycodes=us&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (seq !== geocodeSeq) return; // a newer keystroke superseded this query
    if (!res.ok) { closeLocationResults(); return; }
    const data = (await res.json()) as any[];
    renderLocationResults(data.map((r) => ({
      label: String(r.display_name),
      go: () => {
        const b = r.boundingbox as string[] | undefined; // [s, n, w, e]
        if (b && b.length === 4) map.fitBounds([[+b[0], +b[2]], [+b[1], +b[3]]]);
        else map.setView([+r.lat, +r.lon], 14);
      },
    })));
  } catch {
    if (seq === geocodeSeq) closeLocationResults();
  }
}

locationInput.addEventListener("input", () => {
  window.clearTimeout(geocodeDebounce);
  const q = locationInput.value.trim();
  if (!q) { closeLocationResults(); return; }
  // Nominatim asks for <=1 req/sec; 400ms debounce keeps the typeahead polite.
  geocodeDebounce = window.setTimeout(() => runGeocode(q), 400);
});
locationInput.addEventListener("keydown", (e) => {
  const rows = Array.from(locationResults.querySelectorAll(".location-result")) as HTMLElement[];
  if (!rows.length) return;
  let active = rows.findIndex((r) => r.classList.contains("active"));
  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
    e.preventDefault();
    if (active >= 0) rows[active].classList.remove("active");
    active = e.key === "ArrowDown" ? (active + 1) % rows.length
                                   : (active - 1 + rows.length) % rows.length;
    rows[active].classList.add("active");
    rows[active].scrollIntoView({ block: "nearest" });
  } else if (e.key === "Enter") {
    e.preventDefault();
    rows[active >= 0 ? active : 0].click();
  } else if (e.key === "Escape") {
    closeLocationResults();
  }
});
document.addEventListener("click", (e) => {
  const t = e.target as Node;
  if (!locationInput.contains(t) && !locationResults.contains(t)) closeLocationResults();
});

// ===== Layer search — hidden by default, revealed via a toggle =====
const layerSearchToggle = document.getElementById("layer-search-toggle") as HTMLButtonElement;
const layerSearchWrap = document.getElementById("layer-search-wrap")!;
function setLayerSearchOpen(open: boolean): void {
  layerSearchWrap.hidden = !open;
  layerSearchToggle.textContent = open ? "Search layers ▴" : "Search layers ▾";
  localStorage.setItem("mapbeast.layerSearchOpen", String(open));
  if (!open && searchInput.value) { // drop a stale filter when hiding
    searchInput.value = ""; searchQuery = ""; renderPicker();
  }
}
layerSearchToggle.addEventListener("click", () => setLayerSearchOpen(layerSearchWrap.hidden));
setLayerSearchOpen(localStorage.getItem("mapbeast.layerSearchOpen") === "true");

// --- Viewport relevance ----------------------------------------------------
// The picker lists a layer only when it geographically belongs to the current
// view — "hard locked to the viewport, plus a small radius." A layer qualifies
// if it COVERS a real fraction of what you're looking at, OR sits mostly INSIDE
// the viewport padded by a radius — not merely clipping the edge or extending in
// from a neighbouring region. Tune these to taste:
const VIEW_PAD_FRAC = 0.35;     // the "additional radius": pad the viewport this much per side
const MIN_VIEW_COVERED = 0.45;  // qualifies if the layer covers >= this fraction of the (unpadded) view
const MIN_LAYER_IN_VIEW = 0.55; // ...or if >= this fraction of the layer sits inside the padded view
const MIN_LAYER_ON_SCREEN = 0.25; // ...and it must have at least this fraction of ITSELF on the actual
                                //    (unpadded) screen — a map sitting only in the pad (far off-screen at
                                //    low zoom, where the pad is ~30km) is NOT "here" no matter how much of
                                //    it fits within view+radius. Kills the "lists but renders nowhere near
                                //    the view" case (far-west county plats while viewing downtown).
const MIN_REL_SIZE = 0.04;      // ...but a layer fully inside the view must still be >= this fraction of
                                //    the view (keeps zoomed-out continental views from listing tiny quads)
const ZOOM_TINY_OK = 13;        // ...EXCEPT once you're zoomed in this far, a fully-in-view layer lists
                                //    regardless of rel-size — so tiny-coverage maps (e.g. a single Hopkins
                                //    plate covering a few blocks) still surface at city zoom (z13-14), not
                                //    only when you're nearly on top of them. The anti-bloat floor still
                                //    applies below this zoom (continental/regional views stay clean).

function _bArea(b: L.LatLngBounds): number {
  return Math.max(0, b.getNorth() - b.getSouth()) * Math.max(0, b.getEast() - b.getWest());
}
function _interArea(a: L.LatLngBounds, b: L.LatLngBounds): number {
  const s = Math.max(a.getSouth(), b.getSouth()), n = Math.min(a.getNorth(), b.getNorth());
  const w = Math.max(a.getWest(), b.getWest()), e = Math.min(a.getEast(), b.getEast());
  return Math.max(0, n - s) * Math.max(0, e - w);
}

// The current view's derived geometry, computed ONCE per relevance pass and
// threaded through boundsRelevant/visibleInViewport. The old shape re-called
// map.getBounds() + pad(0.35) per LAYER — thousands of LatLngBounds allocations
// on every moveend fingerprint check.
type ViewCtx = { view: L.LatLngBounds; padded: L.LatLngBounds; viewArea: number; zoom: number };
function currentViewCtx(): ViewCtx {
  const view = map.getBounds();
  return { view, padded: view.pad(VIEW_PAD_FRAC), viewArea: _bArea(view), zoom: map.getZoom() };
}

/**
 * Geographic relevance: is this layer actually "here" in the current view?
 * Replaces the old "any bbox intersection" test, which let a layer show whenever
 * its bbox so much as clipped the viewport corner (noisy with thousands of
 * layers across adjacent regions). Now a layer must either fill a real share of
 * the view, or sit mostly within the view+radius.
 */
function boundsRelevant(b: L.LatLngBounds | null, ctx: ViewCtx): boolean {
  if (!b) return true; // world-scope (OSM, Esri base) — always relevant
  const { view, padded, viewArea } = ctx;
  if (!padded.intersects(b)) return false; // not even within the radius — definitely not here
  const layerArea = _bArea(b);
  if (viewArea <= 0 || layerArea <= 0) return true;
  const onScreen = _interArea(b, view);                   // overlap with the ACTUAL (unpadded) view
  const coverOfView = onScreen / viewArea;                // how much of the screen it fills
  const layerOnScreen = onScreen / layerArea;             // how much of the LAYER is actually on-screen
  const layerInView = _interArea(b, padded) / layerArea;  // how much of it is within view+radius
  if (coverOfView >= MIN_VIEW_COVERED) return true;        // fills a real chunk of what you see
  // mostly here — but it must ALSO be substantially ON the actual screen, not just sitting
  // inside the 35% pad. At low zoom the pad is ~30 km of slop, so without this a small map
  // far off-screen (a far-west county plat while you view downtown) counted as "mostly here"
  // and listed while rendering nowhere near the view (the reported bug). Require a real
  // on-screen share; and either big enough vs the view, or zoomed in past ZOOM_TINY_OK.
  if (layerInView >= MIN_LAYER_IN_VIEW && layerOnScreen >= MIN_LAYER_ON_SCREEN &&
      (ctx.zoom >= ZOOM_TINY_OK || layerArea / viewArea >= MIN_REL_SIZE)) return true;
  return false;                                            // mostly elsewhere / edge-clip → hide
}

// boundsRelevant generalized to a set of NON-OVERLAPPING coverage boxes: every
// ratio's numerator/denominator becomes a sum over boxes (exact because the boxes
// don't overlap — see coverage_probe.py, which emits them non-overlapping by
// construction). Thresholds and semantics identical to the single-box case, so a
// one-box coverage list behaves exactly like boundsRelevant on that box.
// NOTE: the threshold constants above are mirrored in the Python relevance sim
// (pipelines/coverage_probe.py) — keep them in lockstep.
function boxesRelevant(boxes: L.LatLngBounds[], ctx: ViewCtx): boolean {
  const { view, padded, viewArea } = ctx;
  let layerArea = 0, onScreen = 0, inPadded = 0, anyNear = false;
  for (const b of boxes) {
    layerArea += _bArea(b);
    if (padded.intersects(b)) {
      // Boxes outside `padded` contribute 0 to both intersection sums anyway —
      // skipping them here only saves the _interArea calls.
      anyNear = true;
      onScreen += _interArea(b, view);
      inPadded += _interArea(b, padded);
    }
  }
  if (!anyNear) return false;
  if (viewArea <= 0 || layerArea <= 0) return true;
  const coverOfView = onScreen / viewArea;
  const layerOnScreen = onScreen / layerArea;
  const layerInView = inPadded / layerArea;
  if (coverOfView >= MIN_VIEW_COVERED) return true;
  if (layerInView >= MIN_LAYER_IN_VIEW && layerOnScreen >= MIN_LAYER_ON_SCREEN &&
      (ctx.zoom >= ZOOM_TINY_OK || layerArea / viewArea >= MIN_REL_SIZE)) return true;
  return false;
}

/** Coverage-aware relevance for one REAL layer id: layers carrying a `coverage`
 *  field are judged by their real-content boxes; everything else keeps the
 *  single-bbox fast path (one Map.get miss per layer per pass — ~20k layers). */
function relevantFor(id: string, ctx: ViewCtx): boolean {
  const cov = layerCoverage.get(id);
  return cov ? boxesRelevant(cov, ctx)
             : boundsRelevant(layerBounds.get(id) ?? null, ctx);
}

function visibleInViewport(layer: CatalogLayer, ctx: ViewCtx): boolean {
  // A date-cell / group spans the UNION of its members, which can straddle gaps
  // where no component actually covers the view (e.g. a Bay-wide "1950s maps"
  // showing over South SF where its members are all in Oakland/San Jose). So it's
  // "here" only if at least one COMPONENT is relevant — not the union bbox.
  if (layer.members && layer.members.length) {
    return layer.members.some((m) => relevantFor(m.id, ctx));
  }
  return relevantFor(layer.id, ctx);
}

/** One pass over displayLayers: ids passing the viewport + zoom filters for the
 * current view. Shared by renderPicker and the moveend fingerprint so a single
 * map move does this exactly once (the old flow filtered in renderPicker, then
 * re-filtered in pickerFingerprint — 2-3 full O(n) passes per move). */
function computeVisibleIds(ctx: ViewCtx): Set<string> {
  const out = new Set<string>();
  for (const l of displayLayers) {
    if (visibleInViewport(l, ctx) && relevantAtZoom(l, ctx.zoom)) out.add(l.id);
  }
  return out;
}

/** Hard zoom-range gate only; geographic relevance lives in visibleInViewport(). */
function relevantAtZoom(layer: CatalogLayer, zoom: number): boolean {
  if (layer.min_zoom != null && zoom < layer.min_zoom - 2) return false;
  return true;
}

/** Returns "" if in range, otherwise a human reason. */
function zoomRangeIssue(layer: CatalogLayer, zoom: number): string {
  if (layer.max_zoom != null && zoom > layer.max_zoom) {
    return `Zoom out to ${layer.max_zoom} or less — this layer has no tiles past zoom ${layer.max_zoom}.`;
  }
  if (layer.min_zoom != null && zoom < layer.min_zoom) {
    return `Zoom in to ${layer.min_zoom} or more — this layer has no tiles below zoom ${layer.min_zoom}.`;
  }
  return "";
}

function matchesSearch(layer: CatalogLayer, q: string): boolean {
  if (!q) return true;
  // Place names live in titles/attribution (there's no region field). Include the
  // component layers' titles so searching a specific map (e.g. "Rochester") surfaces
  // the date-cell that contains it.
  const members = layer.members
    ? " " + layer.members.map((m) => `${m.title} ${m.year} ${m.attribution}`).join(" ")
    : "";
  const haystack = `${layer.title} ${layer.year} ${layer.category} ${layer.attribution}${members}`.toLowerCase();
  return haystack.includes(q);
}

function buildLayerRow(entry: CatalogLayer): HTMLDivElement {
  const row = document.createElement("div");
  row.className = "layer-row";
  const isActive = activeLayers.has(entry.id);
  if (isActive) row.classList.add("active");
  row.dataset.id = entry.id;

  const issue = zoomRangeIssue(entry, map.getZoom());
  if (issue) {
    row.classList.add("out-of-range");
    row.title = issue;
  }

  const label = document.createElement("label");
  const cb = document.createElement("input");
  cb.type = "checkbox";
  cb.checked = isActive;
  cb.addEventListener("change", () => {
    if (cb.checked) activateLayer(entry, entry.default_opacity ?? 1.0, true);
    else deactivateLayer(entry.id);
  });
  const titleSpan = document.createElement("span");
  titleSpan.className = "title";
  titleSpan.textContent = entry.title;
  const yearSpan = document.createElement("span");
  yearSpan.className = "year";
  yearSpan.textContent = String(entry.year);
  label.append(cb, titleSpan, yearSpan);
  // In side-by-side mode, mark each active row as L or R so the user can tell
  // which one's on which side of the swipe divider.
  if (isActive && swipePair) {
    const sideIdx = swipePair.indexOf(entry.id);
    if (sideIdx === 0 || sideIdx === 1) {
      const tag = document.createElement("span");
      tag.className = "swipe-side-tag";
      tag.textContent = sideIdx === 0 ? "left" : "right";
      label.appendChild(tag);
    }
  }
  // For active layers past max_zoom, mark them as upscaled so the user knows
  // they're seeing blurry overviews rather than native tiles.
  if (isActive && issue) {
    const upscaled = document.createElement("span");
    upscaled.className = "upscaled-tag";
    upscaled.textContent = "upscaled";
    upscaled.title = issue;
    label.appendChild(upscaled);
  }

  const opacityWrap = document.createElement("div");
  opacityWrap.className = "opacity-wrap";
  const opacityLabel = document.createElement("span");
  opacityLabel.className = "opacity-label";
  const opacity = document.createElement("input");
  opacity.type = "range";
  opacity.min = "0"; opacity.max = "1"; opacity.step = "0.05";
  const current = activeLayers.get(entry.id)?.opacity ?? entry.default_opacity ?? 1.0;
  opacity.value = String(current);
  opacityLabel.textContent = `${Math.round(current * 100)}%`;
  opacity.addEventListener("input", () => {
    const v = Number(opacity.value);
    opacityLabel.textContent = `${Math.round(v * 100)}%`;
    setOpacity(entry.id, v);
  });
  opacityWrap.append(opacity, opacityLabel);

  const attr = document.createElement("div");
  attr.className = "attr";
  const attrText = entry.vintage ? `${entry.attribution} · ${entry.vintage}` : entry.attribution;
  attr.textContent = attrText;

  row.append(label, opacityWrap, attr);

  // For a date-cell, explicitly list its component layer(s) underneath so the user
  // sees exactly what "1900s maps" is — even when it holds a single map.
  if (entry.members && entry.members.length >= 1) {
    const list = document.createElement("ul");
    list.className = "member-list";
    for (const m of [...entry.members].sort((a, b) => Number(b.year) - Number(a.year))) {
      const li = document.createElement("li");
      li.className = "member-item";
      const mt = document.createElement("span");
      mt.className = "member-title";
      mt.textContent = m.title;
      const my = document.createElement("span");
      my.className = "member-year";
      my.textContent = String(m.year);
      li.append(my, mt);
      list.appendChild(li);
    }
    row.appendChild(list);
  }
  return row;
}

/** Sort: "current" first, then years descending. */
function sortLayers(layers: CatalogLayer[]): void {
  layers.sort((a, b) => {
    // Composite coverage cells / groups list ABOVE individual non-composite layers.
    const aComposite = a.members ? 1 : 0, bComposite = b.members ? 1 : 0;
    if (aComposite !== bComposite) return bComposite - aComposite;
    const aIsCurrent = a.year === "current";
    const bIsCurrent = b.year === "current";
    if (aIsCurrent && !bIsCurrent) return -1;
    if (bIsCurrent && !aIsCurrent) return 1;
    if (aIsCurrent && bIsCurrent) return a.title.localeCompare(b.title);
    return Number(b.year) - Number(a.year);
  });
}

function appendActiveSection(layers: CatalogLayer[]): void {
  // Always clear the pinned slot — emit nothing if no active layers.
  activeSection.innerHTML = "";
  if (!layers.length) return;
  const section = document.createElement("div");
  section.className = "section-active";
  const h = document.createElement("div");
  h.className = "section-header";
  const t = document.createElement("span");
  t.innerHTML = `Active <span class="zoom-tag">· zoom ${map.getZoom()}</span>`;
  const c = document.createElement("span"); c.className = "count"; c.textContent = String(layers.length);
  h.append(t, c);
  section.appendChild(h);
  sortLayers(layers);
  for (const entry of layers) section.appendChild(buildLayerRow(entry));
  activeSection.appendChild(section);
}

// Hard cap on rows rendered into the DOM per category section. The viewport-
// filtered case is naturally small, but "show all" (or a broad search) over a
// 3000-layer catalog would otherwise build thousands of rows on every render —
// enough DOM to jank pan/zoom. We render the first N and append a notice.
const MAX_ROWS_PER_CATEGORY = 300;

// Per-section collapsed state, persisted across reloads. A collapsed section
// renders just its header (no rows built at all — a real perf win for the
// huge HTMC section). Keyed by the section key passed to appendCategorySection.
const collapsedSections = new Set<string>(
  JSON.parse(localStorage.getItem("mapbeast.collapsedSections") || "[]")
);
function toggleSection(key: string): void {
  if (collapsedSections.has(key)) collapsedSections.delete(key);
  else collapsedSections.add(key);
  localStorage.setItem("mapbeast.collapsedSections", JSON.stringify([...collapsedSections]));
  renderPicker();
}

function appendCategorySection(
  key: string,
  title: string,
  layers: CatalogLayer[],
): void {
  const section = document.createElement("div");
  section.className = "section-category";
  section.dataset.category = key;
  const collapsed = collapsedSections.has(key);
  if (collapsed) section.classList.add("collapsed");

  const h = document.createElement("div");
  h.className = "section-header";
  h.addEventListener("click", () => toggleSection(key));
  const t = document.createElement("span");
  t.textContent = `${collapsed ? "▸" : "▾"} ${title}`;
  const c = document.createElement("span"); c.className = "count"; c.textContent = String(layers.length);
  h.append(t, c);
  section.appendChild(h);

  // Collapsed: header only — don't build any rows.
  if (collapsed) { picker.appendChild(section); return; }

  // Budget of rows we'll actually put in the DOM for this section.
  let rowBudget = MAX_ROWS_PER_CATEGORY;
  const total = layers.length;

  if (!layers.length) {
    const empty = document.createElement("div");
    empty.className = "empty-msg";
    empty.textContent = "—";
    section.appendChild(empty);
  } else {
    // Flat, date-sorted list. There is no region grouping — locality comes from the
    // viewport filter (default view) and the geometric date-cells; "show all" is just
    // a flat searchable escape hatch.
    const sorted = [...layers];
    sortLayers(sorted);
    for (const entry of sorted) {
      if (rowBudget <= 0) break;
      section.appendChild(buildLayerRow(entry));
      rowBudget--;
    }
  }

  // If we hit the cap, tell the user how to narrow the list rather than
  // silently dropping layers.
  if (total > MAX_ROWS_PER_CATEGORY) {
    const more = document.createElement("div");
    more.className = "empty-msg";
    more.textContent = `…${total - MAX_ROWS_PER_CATEGORY} more — refine search to see them.`;
    section.appendChild(more);
  }
  picker.appendChild(section);
}

function renderPicker(precomputedVisibleIds?: Set<string>): void {
  picker.innerHTML = "";

  const q = searchQuery;
  const showAll = showAllToggle.checked;

  const activeIds = new Set(activeLayers.keys());
  // Build the active list from the actual active SLOTS (their real entry), not by
  // filtering displayLayers — a layer activated by id (e.g. a permalink/coverage
  // link) may be a member folded into an auto date-cell and therefore absent from
  // displayLayers, which would leave the active section wrongly empty.
  const active = [...activeLayers.values()].map((v) => v.entry).filter((l) => matchesSearch(l, q));
  const available = displayLayers.filter((l) => !activeIds.has(l.id) && matchesSearch(l, q));

  // Active section (pinned, never hidden by viewport filter)
  appendActiveSection(active);

  // Filter available by viewport AND zoom relevance (when "show all" is off).
  // Zoom relevance drops layers that are way out of useful scale — e.g. when
  // viewing all of New England, drop 1:24k quads that only have useful content
  // at street zoom. maybeRenderPicker passes the visible-id set it already
  // computed for the fingerprint check; other callers let us compute it here.
  const visibleIds = showAll ? undefined : precomputedVisibleIds ?? computeVisibleIds(currentViewCtx());
  const filtered = showAll
    ? available
    : available.filter((l) => visibleIds!.has(l.id));

  // Suffix the section title with the current filter context.
  const suffix = (s: string) =>
    q ? `${s} (matching “${q}”)` : !showAll ? `${s} (in view)` : s;

  // Group by category in canonical order. The "historical" category is split
  // into hand-curated historical maps vs the bulk USGS HTMC topo quads — the
  // HTMC set is ~2700 layers and dominates the picker, so it gets its own
  // (collapsible) section.
  const isHtmc = (l: CatalogLayer) => l.id.startsWith("htmc-");
  for (const cat of CATEGORY_ORDER) {
    const inCat = filtered.filter((l) => l.category === cat);
    if (cat === "historical") {
      appendCategorySection("historical", suffix("Historical maps"), inCat.filter((l) => !isHtmc(l)));
      appendCategorySection("htmc", suffix("USGS HTMC topo"), inCat.filter(isHtmc));
    } else {
      appendCategorySection(cat, suffix(CATEGORY_TITLE[cat]), inCat);
    }
  }

  // Keep the move-handler fingerprint in sync so the next moveend doesn't
  // spuriously skip a real re-render. Reuses this render's visible-id set —
  // no second filter pass.
  lastPickerFingerprint = pickerFingerprint(visibleIds);
}

// Re-render when the map moves or zooms (debounced + fingerprinted).
// Pan within the same set of in-view layers shouldn't trigger a full
// picker rebuild — at 700+ catalog entries that's noticeable on slow
// devices. Cheap fingerprint of (zoom, in-view ids, active ids) lets us
// skip identical re-renders.
let moveDebounce: number | undefined;
let lastPickerFingerprint = "";

// Order-independent rolling hash over a set of layer ids. With ~3000 in-view
// layers the old fingerprint joined every id into a ~60KB string twice per
// render; this folds each id into two 32-bit accumulators with no large
// allocation. Order-independent (addition + XOR) so we can skip the sort.
function hashIds(ids: Iterable<string>): string {
  let count = 0;
  let sum = 0; // additive — order independent
  let xorh = 0; // xor of per-id rolling hashes — order independent
  for (const id of ids) {
    count++;
    let h = 5381;
    for (let i = 0; i < id.length; i++) {
      h = ((h << 5) + h + id.charCodeAt(i)) | 0;
    }
    sum = (sum + h) | 0;
    xorh ^= h;
  }
  return `${count}.${sum}.${xorh}`;
}

// Fingerprint of (zoom, in-view ids, active ids). Callers that already hold the
// visible-id set for the current view pass it in; only computed fresh otherwise.
function pickerFingerprint(visibleIds?: Set<string>): string {
  const z = map.getZoom();
  const active = hashIds(activeLayers.keys());
  if (showAllToggle.checked) {
    return `all:z=${z}:q=${searchQuery}:active=${active}`;
  }
  const visible = hashIds(visibleIds ?? computeVisibleIds(currentViewCtx()));
  return `z=${z}:q=${searchQuery}:active=${active}:vis=${visible}`;
}
function maybeRenderPicker(): void {
  // ONE filter pass: compute the visible set, fingerprint from it, and hand the
  // same set to renderPicker so it doesn't re-filter.
  const visibleIds = showAllToggle.checked ? undefined : computeVisibleIds(currentViewCtx());
  const fp = pickerFingerprint(visibleIds);
  if (fp === lastPickerFingerprint) return;
  lastPickerFingerprint = fp;
  renderPicker(visibleIds);
}
map.on("moveend zoomend", () => {
  window.clearTimeout(moveDebounce);
  moveDebounce = window.setTimeout(maybeRenderPicker, 100);
});

/** Cap on simultaneously-active layers. Swipe compares exactly two; more than
 * that gets ambiguous (which two to compare? which order to stack?). When the
 * user activates a third layer, drop the oldest. */
const MAX_ACTIVE_LAYERS = 2;

async function activateLayer(entry: CatalogLayer, opacity: number, userInitiated = false) {
  if (activeLayers.has(entry.id)) return;

  // Enforce cap: if at limit, deactivate the oldest first.
  while (activeLayers.size >= MAX_ACTIVE_LAYERS) {
    const oldestId = activeLayers.keys().next().value;
    if (oldestId === undefined) break;
    const oldest = activeLayers.get(oldestId);
    deactivateLayer(oldestId);
    if (oldest) flashNotice(`Showing 2 layers max — dropped "${oldest.entry.title}".`);
  }

  // Reserve the slot synchronously *before* the await. createLayer() is async
  // (the COG path parses the TIFF IFD first); without a placeholder, two
  // concurrent activateLayer calls would both pass the cap check above and
  // end up with 3+ active layers. The placeholder has a real entry/opacity so
  // syncPermalink stays correct mid-load; layer is filled in once ready.
  const slot = { layers: [] as L.Layer[], entry, opacity };
  activeLayers.set(entry.id, slot);

  try {
    // A GROUP entry fans out to all its member layers (shown as one unit); a
    // normal entry is a single layer. Either way the slot holds an array.
    // allSettled, not all: one dead member (404'd COG, retired service) must not
    // take down the whole unit — activate the healthy members and note the rest.
    // Members share ONE pane, so DOM insertion order IS z-order: whatever is added last
    // draws on top. Catalog order therefore decided stacking, which put merge.py's coarse
    // `-overview` tier (emitted after its detail cluster) ON TOP of the detail plates it
    // was split out of — re-creating the index-overlay problem the split exists to prevent
    // (the overview's paper is transparent_white, but its roads, labels and red index
    // rectangles still draw over the detail). Render BIGGEST-footprint first so wide/coarse
    // members sit underneath and fine detail wins where it exists, with the coarse layer
    // filling the gaps. Equal-area members keep their catalog order (sort is stable).
    const bboxArea = (l: CatalogLayer): number => {
      const b = l.bounds as unknown as [[number, number], [number, number]] | undefined;
      if (!b) return Infinity;          // world-scope -> always at the bottom
      return Math.abs((b[1][0] - b[0][0]) * (b[1][1] - b[0][1]));
    };
    const sources = entry.members
      ? [...entry.members].sort((a, b) => bboxArea(b) - bboxArea(a))
      : [entry];
    const settled = await Promise.allSettled(sources.map((s) => createLayer(s, { pane: OVERLAY_PANE })));
    const layers = settled.filter((r): r is PromiseFulfilledResult<L.Layer> => r.status === "fulfilled").map((r) => r.value);
    const failed = sources.filter((_, i) => settled[i].status === "rejected");
    for (const [i, r] of settled.entries()) {
      if (r.status === "rejected") console.error(`Failed to load member ${sources[i].id} of ${entry.id}:`, r.reason);
    }
    if (layers.length === 0) throw (settled[0] as PromiseRejectedResult).reason;
    // The slot may have been deactivated while createLayer was awaiting —
    // if our placeholder is gone, abandon this load instead of resurrecting it.
    if (activeLayers.get(entry.id) !== slot) {
      for (const l of layers) if ("remove" in l) (l as any).remove();
      return;
    }
    if (failed.length > 0) {
      flashNotice(`"${entry.title}": ${failed.length} of ${sources.length} member layer(s) failed to load and were skipped.`);
    }
    for (const l of layers) {
      l.addTo(map);
      if ("setOpacity" in l) (l as any).setOpacity(opacity);
      watchLoading(l);
    }
    slot.layers = layers;

    // Auto-fit only when the user explicitly toggled this layer on AND the
    // current view does not already overlap its bounds. This avoids yanking
    // the camera on permalink-restore (line 527) or default OSM (line 536),
    // and keeps it stable when toggling a layer that's already in view.
    if (userInitiated && entry.bounds) {
      const layerBounds = L.latLngBounds(entry.bounds as L.LatLngBoundsLiteral);
      if (!map.getBounds().intersects(layerBounds)) {
        map.fitBounds(layerBounds);
      }
    }

    // Zoom-range nudge — only when user toggled this on (same rationale as fitBounds above)
    if (userInitiated) {
      const z = map.getZoom();
      if (entry.max_zoom != null && z > entry.max_zoom) {
        map.setZoom(entry.max_zoom);
        flashNotice(`"${entry.title}" only has tiles to zoom ${entry.max_zoom} — zoomed out.`);
      } else if (entry.min_zoom != null && z < entry.min_zoom) {
        map.setZoom(entry.min_zoom);
        flashNotice(`"${entry.title}" requires zoom ≥ ${entry.min_zoom} — zoomed in.`);
      }
    }

    syncPermalink();
    refreshSwipeButton();
    renderPicker();
  } catch (e) {
    // Free the reserved slot so the cap count is accurate and the user can retry.
    if (activeLayers.get(entry.id) === slot) activeLayers.delete(entry.id);
    console.error(`Failed to activate ${entry.id}:`, e);
    // flashNotice, not alert(): a modal steals focus, and this path also fires
    // during permalink restore with no user action.
    flashNotice(`Failed to load layer "${entry.title}".`);
    renderPicker();
  }
}

function deactivateLayer(id: string) {
  const item = activeLayers.get(id);
  if (!item) return;
  // item.layers may be empty if still loading (placeholder slot); activateLayer
  // detects the vanished slot post-await and abandons the load.
  for (const l of item.layers) map.removeLayer(l);
  activeLayers.delete(id);
  if (swipePair?.includes(id)) clearSwipe();
  syncPermalink();
  refreshSwipeButton();
  renderPicker();
}

let noticeTimeout: number | undefined;
function flashNotice(msg: string): void {
  let el = document.getElementById("notice");
  if (!el) {
    el = document.createElement("div");
    el.id = "notice";
    document.body.appendChild(el);
  }
  el.textContent = msg;
  el.classList.add("show");
  window.clearTimeout(noticeTimeout);
  noticeTimeout = window.setTimeout(() => el!.classList.remove("show"), 3500);
}

// Surface the first per-layer COG tile-decode failure (mask-band COGs,
// unsupported photometric, etc.) to the user — layers.ts reports once per layer.
setDecodeErrorSink((_id, title, err) => {
  console.error(`COG decode error for "${title}":`, err);
  flashNotice(`"${title}" failed to render — the COG may be in an unsupported format.`);
});

function setOpacity(id: string, value: number) {
  const item = activeLayers.get(id);
  if (!item) return;
  item.opacity = value;
  for (const l of item.layers) if ("setOpacity" in l) (l as any).setOpacity(value);
  syncPermalink();
}

// ----- Mode selector: Overlay / Side-by-side -----
const overlayBtn = document.getElementById("mode-overlay") as HTMLButtonElement;
const swipeBtn = document.getElementById("mode-swipe") as HTMLButtonElement;

overlayBtn.addEventListener("click", () => {
  if (!swipeControl) return; // already in overlay mode
  clearSwipe();
});

swipeBtn.addEventListener("click", () => {
  if (swipeControl) return; // already in swipe mode
  const ids = Array.from(activeLayers.keys());
  if (ids.length !== 2) return; // disabled state should prevent this anyway
  setSwipe([ids[0], ids[1]]);
});

function setSwipe(pair: [string, string]) {
  clearSwipe();
  // Pass the layer ARRAYS — our patched side-by-side (lib/leaflet-side-by-side)
  // clips EVERY on-map member of each side, so a grouped unit swipes as one side.
  // (Upstream 2.2.0 only clipped one member; see the PATCH note in that file.)
  const left = activeLayers.get(pair[0])?.layers;
  const right = activeLayers.get(pair[1])?.layers;
  if (!left?.length || !right?.length) return;
  // @ts-ignore — leaflet-side-by-side has no types
  swipeControl = L.control.sideBySide(left, right).addTo(map);
  swipePair = pair;
  refreshSwipeButton();
  renderPicker(); // refresh L/R indicators on active rows
  syncPermalink();
}

function clearSwipe() {
  if (swipeControl) {
    swipeControl.remove();
    swipeControl = null;
  }
  swipePair = null;
  refreshSwipeButton();
  renderPicker(); // remove L/R indicators
  syncPermalink();
}

function refreshSwipeButton() {
  const n = activeLayers.size;
  const inSwipe = !!swipeControl;
  overlayBtn.classList.toggle("active", !inSwipe);
  swipeBtn.classList.toggle("active", inSwipe);
  // Side-by-side requires exactly 2 active layers
  swipeBtn.disabled = !inSwipe && n !== 2;
}

// ----- Permalink -----
map.on("moveend zoomend", syncPermalink);

function syncPermalink() {
  const state: PermalinkState = {
    center: map.getCenter(),
    zoom: map.getZoom(),
    activeLayers: Array.from(activeLayers.values()).map((a) => ({ id: a.entry.id, opacity: a.opacity })),
    swipe: swipePair,
  };
  writePermalink(state);
}

// ----- Panel toggle (collapse/expand sidebar) -----
const panelToggle = document.getElementById("panel-toggle") as HTMLButtonElement;
panelToggle.addEventListener("click", () => {
  const collapsed = document.body.classList.toggle("panel-collapsed");
  panelToggle.textContent = collapsed ? "‹" : "›";
  panelToggle.title = collapsed ? "Show panel" : "Hide panel";
  panelToggle.setAttribute("aria-label", panelToggle.title);
  localStorage.setItem("mapbeast.panelCollapsed", String(collapsed));
  // Leaflet needs to know the map container resized, otherwise tiles
  // around the right edge won't fill in when the panel opens/closes.
  setTimeout(() => map.invalidateSize(), 210);
});
// Restore last state
if (localStorage.getItem("mapbeast.panelCollapsed") === "true") {
  document.body.classList.add("panel-collapsed");
  panelToggle.textContent = "‹";
  panelToggle.title = "Show panel";
  panelToggle.setAttribute("aria-label", panelToggle.title);
}

document.getElementById("copy-link")!.addEventListener("click", async () => {
  const btn = document.getElementById("copy-link") as HTMLButtonElement;
  try {
    // navigator.clipboard is unavailable / rejects on insecure (non-HTTPS)
    // contexts and when the permission is denied — don't let it throw silently.
    await navigator.clipboard.writeText(window.location.href);
    const orig = btn.textContent;
    btn.textContent = "Copied!";
    setTimeout(() => (btn.textContent = orig), 1200);
  } catch {
    flashNotice("Couldn't copy automatically — copy the URL from the address bar.");
  }
});

// ----- Help / About modal -----
const helpOverlay = document.getElementById("help-overlay")!;
document.getElementById("help-link")!.addEventListener("click", () => {
  helpOverlay.hidden = false;
});
document.getElementById("help-close")!.addEventListener("click", () => {
  helpOverlay.hidden = true;
});
helpOverlay.addEventListener("click", (e) => {
  if (e.target === helpOverlay) helpOverlay.hidden = true; // backdrop click, not modal content
});
document.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && !helpOverlay.hidden) helpOverlay.hidden = true;
});

// Initial render
renderPicker();

// Restore active layers from permalink. Backwards-compat: if old base= is set,
// activate that as a normal layer too.
const toRestore: { id: string; opacity: number }[] = pl.activeLayers ? [...pl.activeLayers] : [];
if (pl.baseId && !toRestore.some((r) => r.id === pl.baseId)) {
  // Old permalinks had base= + layers=. The base should appear first so it's
  // at the bottom of the visual stack.
  toRestore.unshift({ id: pl.baseId, opacity: 1.0 });
}
if (toRestore.length) {
  for (const a of toRestore) {
    const entry = unitById.get(a.id); // resolves real layers AND group units
    if (entry) await activateLayer(entry, a.opacity);
  }
  if (pl.swipe) {
    const [a, b] = pl.swipe;
    if (activeLayers.has(a) && activeLayers.has(b)) setSwipe([a, b]);
  }
} else {
  // Default: turn on OSM if nothing in permalink
  const osm = catalog.layers.find((l) => l.id === "osm");
  if (osm) await activateLayer(osm, 1.0);
}

refreshSwipeButton();
syncPermalink();

// Apply a permalink that arrives *after* load — e.g. the user pastes a
// permalink into the address bar while already on the site. The browser
// treats that as a same-document navigation (hash change, no reload), so
// without this listener the pasted URL would silently do nothing.
let applyingHash = false;
window.addEventListener("hashchange", async () => {
  if (applyingHash) return; // ignore our own syncPermalink writes
  const next = readPermalink();
  // Only act on a hash that actually carries restorable state.
  if (!next.activeLayers && !next.center) return;
  applyingHash = true;
  try {
    if (next.center && next.zoom != null) map.setView(next.center, next.zoom);
    // Only touch layer state when the hash carries a `layers=` param at all.
    // A view-only hash (ll= but no layers=) means "go here", not "and drop every
    // active layer" — activeLayers is undefined for absent vs [] for explicitly
    // empty (see readPermalink).
    if (next.activeLayers) {
      const wanted = new Map(next.activeLayers.map((l) => [l.id, l.opacity]));
      // Deactivate layers no longer in the permalink.
      for (const id of [...activeLayers.keys()]) {
        if (!wanted.has(id)) deactivateLayer(id);
      }
      // Activate (or re-opacity) layers the permalink asks for.
      for (const [id, opacity] of wanted) {
        const existing = activeLayers.get(id);
        if (existing) {
          if (existing.opacity !== opacity) {
            existing.opacity = opacity;
            for (const l of existing.layers) if ("setOpacity" in l) (l as any).setOpacity(opacity);
          }
        } else {
          const entry = unitById.get(id); // real layer or group unit
          if (entry) await activateLayer(entry, opacity);
        }
      }
      if (next.swipe) {
        const [a, b] = next.swipe;
        if (activeLayers.has(a) && activeLayers.has(b)) setSwipe([a, b]);
      }
    }
    renderPicker();
  } finally {
    applyingHash = false;
  }
});
